"use client";

import { Card } from "@/components/ui/card";
import { type ChartConfig, ChartContainer } from "@/components/ui/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CrimeStats } from "@/lib/crime";
import type { Property } from "@/schemas/property";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Info,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

interface PropertySafetyProps {
  crimeData: CrimeStats | null;
  property: Property;
}

const SAFETY_COLORS = {
  EXCELLENT: "#22c55e", // Green
  GOOD: "#84cc16", // Lime
  AVERAGE: "#eab308", // Yellow
  BELOW_AVERAGE: "#f97316", // Orange
  POOR: "#ef4444", // Red
} as const;

const CRIME_CATEGORIES = {
  "violent-crime": {
    label: "Violent Crime",
    color: "#ef4444", // Red
  },
  homicide: {
    label: "Homicide",
    color: "#b91c1c", // Dark Red
  },
  rape: {
    label: "Sexual Assault",
    color: "#7f1d1d", // Darker Red
  },
  robbery: {
    label: "Robbery",
    color: "#f97316", // Orange
  },
  "aggravated-assault": {
    label: "Aggravated Assault",
    color: "#c2410c", // Dark Orange
  },
  "property-crime": {
    label: "Property Crime",
    color: "#0ea5e9", // Sky Blue
  },
  burglary: {
    label: "Burglary",
    color: "#0369a1", // Dark Sky Blue
  },
  larceny: {
    label: "Larceny",
    color: "#0284c7", // Darker Sky Blue
  },
  "motor-vehicle-theft": {
    label: "Vehicle Theft",
    color: "#075985", // Darkest Sky Blue
  },
} as const;

function getSafetyColor(score: number): string {
  if (score >= 80) return SAFETY_COLORS.EXCELLENT;
  if (score >= 60) return SAFETY_COLORS.GOOD;
  if (score >= 40) return SAFETY_COLORS.AVERAGE;
  if (score >= 20) return SAFETY_COLORS.BELOW_AVERAGE;
  return SAFETY_COLORS.POOR;
}

function getSafetyIcon(score: number) {
  if (score >= 80) return <ShieldCheck className="h-12 w-12" />;
  if (score >= 60) return <Shield className="h-12 w-12" />;
  if (score >= 40) return <ShieldAlert className="h-12 w-12" />;
  if (score >= 20) return <AlertTriangle className="h-12 w-12" />;
  return <ShieldQuestion className="h-12 w-12" />;
}

function getSafetyLabel(score: number): string {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  if (score >= 40) return "Average";
  if (score >= 20) return "Below Average";
  return "Poor";
}

function formatComparisonText(value: number): string {
  if (value > 0) return `${value.toFixed(1)}% higher`;
  if (value < 0) return `${Math.abs(value).toFixed(1)}% lower`;
  return "Same as";
}

// New mobile visualization component
function MobileCrimeComparison({
  data,
  agencyCity,
  stateAbbr,
}: {
  data: Array<{
    name: string;
    key: string;
    Local: number;
    State: number;
    National: number;
  }>;
  agencyCity: string;
  stateAbbr: string;
}) {
  return (
    <div className="space-y-6">
      {data.map((item) => {
        // Find max value for this specific crime type for relative scaling
        const maxValue = Math.max(item.Local, item.State, item.National);
        const localPercentage = (item.Local / maxValue) * 100;
        const statePercentage = (item.State / maxValue) * 100;
        const nationalPercentage = (item.National / maxValue) * 100;
        const stateComparison = (item.Local / item.State - 1) * 100;
        const isHigher = item.Local > item.State;

        return (
          <div key={item.key} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-semibold text-sm">{item.name}</h5>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>Rate per 100,000 residents</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Visual bar comparison */}
            <div className="space-y-2.5">
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 w-20">{agencyCity}</span>
                  <div className="flex-1 mx-4">
                    <div className="h-5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-plaster rounded-full transition-all duration-500"
                        style={{ width: `${localPercentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="font-semibold w-16 text-right text-gray-900">
                    {item.Local.toFixed(1)}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 w-20">{stateAbbr}</span>
                  <div className="flex-1 mx-4">
                    <div className="h-5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-plaster-dark rounded-full transition-all duration-500"
                        style={{ width: `${statePercentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="font-semibold w-16 text-right text-gray-900">
                    {item.State.toFixed(1)}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 w-20">USA</span>
                  <div className="flex-1 mx-4">
                    <div className="h-5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-darkGray rounded-full transition-all duration-500"
                        style={{ width: `${nationalPercentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="font-semibold w-16 text-right text-gray-900">
                    {item.National.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Comparison badge */}
            <div className="mt-3 flex items-center gap-2">
              <div
                className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1
                  ${isHigher ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}
              >
                {isHigher ? (
                  <ArrowUp className="w-3 h-3" />
                ) : (
                  <ArrowDown className="w-3 h-3" />
                )}
                {Math.abs(stateComparison).toFixed(1)}%{" "}
                {isHigher ? "higher" : "lower"} than state average
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function extractCityFromAgencyName(agencyName: string): string {
  // Most agency names are in format "City Police Department" or "City County Sheriff"
  const name = agencyName
    .split(" Police")[0]
    .split(" County")[0]
    .split(" Sheriff")[0];
  return name.trim();
}

export function PropertySafety({ crimeData, property }: PropertySafetyProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    Object.keys(CRIME_CATEGORIES),
  );
  const [isMobile, setIsMobile] = useState(false);

  // Add useEffect for mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!crimeData) return null;

  const safetyColor = getSafetyColor(crimeData.safetyScore ?? 0);
  const safetyIcon = getSafetyIcon(crimeData.safetyScore ?? 0);
  const safetyLabel = getSafetyLabel(crimeData.safetyScore ?? 0);

  // Prepare data for the chart
  const chartData = Object.entries(CRIME_CATEGORIES)
    .filter(([key]) => crimeData.rateComparisons?.[key])
    .map(([key, category]) => {
      const comparison = crimeData.rateComparisons?.[key];
      if (!comparison) return null;

      return {
        name: category.label,
        key,
        Local: comparison.agency,
        State: comparison.state,
        National: comparison.national,
      };
    })
    .filter((d): d is NonNullable<typeof d> => d !== null);

  const chartConfig: ChartConfig = {
    crime: {
      label: "Crime Rates",
      color: "#CF9D7C",
    },
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Safety Analysis</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              Safety scores range from 1-100, with higher scores indicating
              safer areas
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-8">
        {/* Safety Score Card */}
        <div className="flex items-start gap-6">
          <div
            style={{ color: safetyColor }}
            className="p-4 rounded-xl bg-gray-50"
          >
            {safetyIcon}
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-3 mb-2">
              <div className="text-3xl font-semibold">
                {crimeData.safetyScore}
                <span className="text-lg text-gray-500">/100</span>
              </div>
              <div
                style={{
                  color: safetyColor,
                  backgroundColor: `${safetyColor}15`,
                }}
                className="px-3 py-1 rounded-full text-sm font-semibold"
              >
                {safetyLabel}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Based on data from {crimeData.agency.agency_name}
            </p>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-gray-50">
            <div className="text-sm text-gray-500 mb-1">Total Crime Rate</div>
            <div className="text-xl font-semibold">
              {crimeData.crimeRate.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">per 100,000 residents</div>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <div className="text-sm text-gray-500 mb-1">Violent Crime Rate</div>
            <div className="text-xl font-semibold">
              {crimeData.violentCrimeRate.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">per 100,000 residents</div>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <div className="text-sm text-gray-500 mb-1">
              Property Crime Rate
            </div>
            <div className="text-xl font-semibold">
              {crimeData.propertyCrimeRate.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">per 100,000 residents</div>
          </div>
        </div>

        {/* Crime Rate Comparisons - Conditional Rendering */}
        {chartData.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-semibold">Crime Rate Comparison</h4>
            </div>

            {/* Desktop Chart */}
            {!isMobile && (
              <div className="hidden md:block h-[400px] w-full">
                <ChartContainer config={chartConfig}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData.filter((d) =>
                        selectedCategories.includes(d.key),
                      )}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis
                        tickFormatter={(value) =>
                          typeof value === "number" ? value.toFixed(0) : value
                        }
                        tick={{ fontSize: 12 }}
                        label={{
                          value: "Rate per 100,000 residents",
                          angle: -90,
                          position: "insideLeft",
                          style: { fontSize: 12 },
                        }}
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;

                          return (
                            <div className="rounded-lg border bg-background p-3 shadow-sm">
                              <div className="font-semibold mb-2">{label}</div>
                              {payload.map((entry) => (
                                <div
                                  key={entry.name}
                                  className="flex items-center justify-between gap-4 text-sm"
                                >
                                  <span
                                    style={{ color: entry.color }}
                                    className="font-semibold"
                                  >
                                    {entry.name}:
                                  </span>
                                  <span>
                                    {typeof entry.value === "number"
                                      ? entry.value.toFixed(1)
                                      : entry.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        }}
                      />
                      <Legend
                        onClick={(e) => {
                          const category = e.dataKey as string;
                          setSelectedCategories((prev) =>
                            prev.includes(category)
                              ? prev.filter((c) => c !== category)
                              : [...prev, category],
                          );
                        }}
                        wrapperStyle={{ fontSize: 12, cursor: "pointer" }}
                        content={({ payload }) => {
                          if (!payload) return null;

                          const toggleCategory = (category: string) => {
                            setSelectedCategories((prev) =>
                              prev.includes(category)
                                ? prev.filter((c) => c !== category)
                                : [...prev, category],
                            );
                          };

                          return (
                            <div className="flex items-center justify-center gap-6 mt-4">
                              {payload.map((entry) => (
                                <div
                                  key={entry.value}
                                  role="button"
                                  tabIndex={0}
                                  className="flex items-center gap-2 cursor-pointer transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-plaster rounded px-2 py-1"
                                  onClick={() =>
                                    toggleCategory(entry.dataKey as string)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      toggleCategory(entry.dataKey as string);
                                    }
                                  }}
                                >
                                  <div
                                    className="w-3 h-3 rounded"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="font-semibold">
                                    {entry.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        }}
                      />
                      <Bar
                        dataKey="Local"
                        name={property.city}
                        fill="#CF9D7C"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="State"
                        name={crimeData.agency.state_abbr}
                        fill="#9D6A4E"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="National"
                        name="USA"
                        fill="#595959"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            )}

            {/* Mobile View */}
            {isMobile && (
              <div className="md:hidden">
                <MobileCrimeComparison
                  data={chartData}
                  agencyCity={property.city}
                  stateAbbr={crimeData.agency.state_abbr}
                />
              </div>
            )}
          </div>
        )}

        {/* Recent Trends */}
        {crimeData.data.length > 0 && (
          <div>
            <h4 className="text-base font-semibold mb-3">
              Recent Crime Trends
            </h4>
            <div className="space-y-3">
              {crimeData.data.map((year) => (
                <div key={year.year} className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-gray-900">
                      {year.year}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Violent Crimes
                      </div>
                      <div className="flex items-baseline gap-2">
                        <div className="text-lg font-semibold text-gray-900">
                          {year.violent_crime.toLocaleString()}
                        </div>
                        {crimeData.data[0] &&
                          year.year !== crimeData.data[0].year && (
                            <div
                              className={`text-xs font-semibold ${
                                year.violent_crime >
                                crimeData.data[0].violent_crime
                                  ? "text-red-600"
                                  : year.violent_crime <
                                      crimeData.data[0].violent_crime
                                    ? "text-green-600"
                                    : "text-gray-500"
                              }`}
                            >
                              {year.violent_crime ===
                              crimeData.data[0].violent_crime ? (
                                "No change"
                              ) : year.violent_crime >
                                crimeData.data[0].violent_crime ? (
                                <span className="inline-flex items-center gap-0.5">
                                  <ArrowUp className="w-3 h-3" />
                                  {(
                                    (year.violent_crime /
                                      crimeData.data[0].violent_crime -
                                      1) *
                                    100
                                  ).toFixed(1)}
                                  %
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5">
                                  <ArrowDown className="w-3 h-3" />
                                  {(
                                    (1 -
                                      year.violent_crime /
                                        crimeData.data[0].violent_crime) *
                                    100
                                  ).toFixed(1)}
                                  %
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Property Crimes
                      </div>
                      <div className="flex items-baseline gap-2">
                        <div className="text-lg font-semibold text-gray-900">
                          {year.property_crime.toLocaleString()}
                        </div>
                        {crimeData.data[0] &&
                          year.year !== crimeData.data[0].year && (
                            <div
                              className={`text-xs font-semibold ${
                                year.property_crime >
                                crimeData.data[0].property_crime
                                  ? "text-red-600"
                                  : year.property_crime <
                                      crimeData.data[0].property_crime
                                    ? "text-green-600"
                                    : "text-gray-500"
                              }`}
                            >
                              {year.property_crime ===
                              crimeData.data[0].property_crime ? (
                                "No change"
                              ) : year.property_crime >
                                crimeData.data[0].property_crime ? (
                                <span className="inline-flex items-center gap-0.5">
                                  <ArrowUp className="w-3 h-3" />
                                  {(
                                    (year.property_crime /
                                      crimeData.data[0].property_crime -
                                      1) *
                                    100
                                  ).toFixed(1)}
                                  %
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5">
                                  <ArrowDown className="w-3 h-3" />
                                  {(
                                    (1 -
                                      year.property_crime /
                                        crimeData.data[0].property_crime) *
                                    100
                                  ).toFixed(1)}
                                  %
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
